import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import AppTable from "../../common/AppTable/AppTable";
import AddBtn from "../../common/AppSpecificConpnents/AddBtn";
import UploadViaCsvBtn from "../../common/AppSpecificConpnents/UploadViaCsvBtn";
import ExportCsvBtn from "../../common/AppSpecificConpnents/ExportCsvBtn";
import DeleteConfirmation from "../../common/Confirmations/DeleteConfirmation";
import Loader from "../../common/Loader";
import { TableActionGroup } from "../../common/Buttons";
import { scrollToTop } from "../../util/AppUtils";
import { showSnackbar } from "../../redux/snackbarSlice";
import {
  createTrainingLog,
  deleteTrainingLog,
  getAllTrainingLogs,
  updateTrainingLog,
} from "../../redux/trainingLogSlice";
import { buildPermissionContext } from "../../util/permissions";
import { unwrapTrainedToPayload } from "../../util/trainingLogStatus";
import TrainingLogSignatureModal from "./TrainingLogSignatureModal";

async function generateSHA256(input) {
  const encoder = new TextEncoder();
  const data = encoder.encode(input);
  const hashBuffer = await crypto.subtle.digest("SHA-256", data);
  return Array.from(new Uint8Array(hashBuffer))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
}

function getDocumentNumber(row) {
  return (
    row.procedure_revision?.reference ??
    row.procedure_revision_ref ??
    row.part_number ??
    ""
  );
}

function getTrainerName(row) {
  if (row.trainer_name) return row.trainer_name;
  if (row.trainer_first_name != null || row.trainer_last_name != null) {
    return `${row.trainer_first_name || ""} ${row.trainer_last_name || ""}`.trim();
  }
  if (row.trainer && typeof row.trainer === "object") {
    const { first_name = "", last_name = "" } = row.trainer;
    const t = `${first_name} ${last_name}`.trim();
    if (t) return t;
  }
  if (typeof row.trainer === "string" && row.trainer.length > 0) {
    return row.trainer;
  }
  return "";
}

function getTraineeName(row) {
  if (row.trainee_name) return row.trainee_name;
  if (row.trainee_first_name != null || row.trainee_last_name != null) {
    return `${row.trainee_first_name || ""} ${row.trainee_last_name || ""}`.trim();
  }
  if (row.trainee && typeof row.trainee === "object") {
    const { first_name = "", last_name = "" } = row.trainee;
    const t = `${first_name} ${last_name}`.trim();
    if (t) return t;
  }
  if (typeof row.trainee === "string" && row.trainee.length > 0) {
    return row.trainee;
  }
  return "";
}

function getRevisionDisplay(row) {
  return row.revision_trained ?? row.rev ?? row.version_id_ref ?? "";
}

function getTrainingStatusDisplay(row) {
  if (row.training_status_of_trainee) return row.training_status_of_trainee;
  const trainedTo = unwrapTrainedToPayload(row.trained_to);
  const steps = trainedTo?.steps;
  if (Array.isArray(steps) && steps.length > 0 && steps[0]?.status) {
    return steps[0].status;
  }
  if (row.is_maintained_outside_system) return "Maintained Outside System";
  return "In System";
}

function splitFullName(name) {
  const t = (name || "").trim();
  if (!t) return { first_name: "", last_name: "" };
  const i = t.indexOf(" ");
  if (i === -1) return { first_name: t, last_name: "" };
  return { first_name: t.slice(0, i), last_name: t.slice(i + 1).trim() };
}

function rowToEditShape(row) {
  return {
    training_log_id: row.training_log_id,
    procedure_revision_ref:
      row.procedure_revision_ref ?? row.part_number ?? "",
    date_of_training: row.date_of_training
      ? String(row.date_of_training).split("T")[0]
      : "",
    trainer_name: getTrainerName(row),
    trainee_name: getTraineeName(row),
    trainer_signature:
      typeof row.trainer_signature === "string" ? row.trainer_signature : "",
    trainee_signature:
      typeof row.trainee_signature === "string" ? row.trainee_signature : "",
    revision_trained: getRevisionDisplay(row),
    training_status_of_trainee: getTrainingStatusDisplay(row),
  };
}

function emptyNewRow() {
  return {
    procedure_revision_ref: "",
    date_of_training: "",
    trainer_name: "",
    trainee_name: "",
    trainer_signature: "",
    trainee_signature: "",
    revision_trained: "",
    training_status_of_trainee: "",
  };
}

function parseTableReqPayload(reqPayload) {
  if (!reqPayload || typeof reqPayload !== "string") {
    return { search: "", searchTypes: [], sortBy: null, sortOrder: null };
  }
  const params = new URLSearchParams(reqPayload);
  const search = params.get("search") || "";
  const searchTypeRaw = params.get("search_type") || "";
  const searchTypes = searchTypeRaw
    ? searchTypeRaw.split(",").filter(Boolean)
    : [];
  return {
    search,
    searchTypes,
    sortBy: params.get("sort_by"),
    sortOrder: params.get("sort_order"),
  };
}

/** Pad/API often returns a data URL or bare base64 string — render as <img>, not script text. */
function signatureStringToImgSrc(s) {
  if (typeof s !== "string" || !s.trim()) return null;
  const t = s.trim();
  if (t.startsWith("data:image/") || t.startsWith("data:application/")) {
    return t;
  }
  const cleaned = t.replace(/\s/g, "");
  if (cleaned.length >= 40 && /^[A-Za-z0-9+/]+=*$/.test(cleaned)) {
    return `data:image/png;base64,${cleaned}`;
  }
  return null;
}

function getCellSearchString(row, field) {
  switch (field) {
    case "document_number":
      return getDocumentNumber(row);
    case "date_of_training":
      return row.date_of_training
        ? String(row.date_of_training).split("T")[0]
        : "";
    case "trainer":
      return getTrainerName(row);
    case "trainer_signature":
      if (typeof row.trainer_signature === "string") {
        return signatureStringToImgSrc(row.trainer_signature)
          ? "signature"
          : row.trainer_signature;
      }
      if (row.trainer_signature?.data?.length) return "signature";
      return "";
    case "trainee":
      return getTraineeName(row);
    case "trainee_signature":
      if (typeof row.trainee_signature === "string") {
        return signatureStringToImgSrc(row.trainee_signature)
          ? "signature"
          : row.trainee_signature;
      }
      if (row.trainee_signature?.data?.length) return "signature";
      return "";
    case "revision_trained":
      return String(getRevisionDisplay(row));
    case "training_status_of_trainee":
      return getTrainingStatusDisplay(row);
    default:
      return "";
  }
}

function sortRows(rows, sortBy, sortOrder) {
  if (!sortBy || !sortOrder || sortOrder === "DEF") return rows;
  const dir = sortOrder === "DESC" ? -1 : 1;
  const copy = [...rows];
  copy.sort((a, b) => {
    const va = getCellSearchString(a, sortBy).toLowerCase();
    const vb = getCellSearchString(b, sortBy).toLowerCase();
    if (va < vb) return -1 * dir;
    if (va > vb) return 1 * dir;
    return 0;
  });
  return copy;
}

/** Edit control in Trainer / Trainee signature cells (design asset). */
function SignatureCellEditIcon({ style }) {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 20 20"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      style={{ flexShrink: 0, display: "block", ...style }}
      aria-hidden
    >
      <path
        d="M17.8376 6.76712L13.1867 2.16438L14.7187 0.630137C15.1382 0.210046 15.6535 0 16.2645 0C16.8755 0 17.3907 0.210046 17.8102 0.630137L19.3423 2.16438C19.7618 2.58447 19.9806 3.09132 19.9989 3.68493C20.0171 4.27854 19.8165 4.78539 19.397 5.20548L17.8376 6.76712ZM16.2508 8.38356L4.6509 20H0V15.3425L11.5999 3.72603L16.2508 8.38356Z"
        fill="#0D6EFD"
      />
    </svg>
  );
}

/** Alias for legacy lucide `Pencil` — prevents "Pencil is not defined" if HMR or old JSX still references it. */
const Pencil = SignatureCellEditIcon;

/** Shared look: light gray script text (matches Training Log design reference). */
const SIGNATURE_TEXT_STYLE = {
  fontFamily:
    "'Segoe Script', 'Brush Script MT', 'Lucida Handwriting', 'Apple Chancery', cursive",
  fontSize: 15,
  color: "#8f9092",
  fontWeight: 400,
  lineHeight: 1.2,
};

/**
 * API often stores signatures as Buffer of:
 * - raw PNG/JPEG bytes, or
 * - UTF-8 "data:image/...;base64,..." or bare base64 ASCII (not raw image bytes).
 */
function decodeSignatureBuffer(bufferLike) {
  if (!bufferLike?.data) return null;
  let bytes;
  if (bufferLike.data instanceof Uint8Array) {
    bytes = bufferLike.data;
  } else if (Array.isArray(bufferLike.data)) {
    bytes = new Uint8Array(bufferLike.data);
  } else {
    return null;
  }
  if (bytes.length === 0) return null;

  // Raw PNG
  if (
    bytes.length >= 4 &&
    bytes[0] === 0x89 &&
    bytes[1] === 0x50 &&
    bytes[2] === 0x4e &&
    bytes[3] === 0x47
  ) {
    let bin = "";
    for (let i = 0; i < bytes.length; i += 1) {
      bin += String.fromCharCode(bytes[i]);
    }
    return { kind: "img", src: `data:image/png;base64,${btoa(bin)}` };
  }

  // Raw JPEG
  if (bytes.length >= 3 && bytes[0] === 0xff && bytes[1] === 0xd8 && bytes[2] === 0xff) {
    let bin = "";
    for (let i = 0; i < bytes.length; i += 1) {
      bin += String.fromCharCode(bytes[i]);
    }
    return { kind: "img", src: `data:image/jpeg;base64,${btoa(bin)}` };
  }

  // GIF
  if (
    bytes.length >= 6 &&
    bytes[0] === 0x47 &&
    bytes[1] === 0x49 &&
    bytes[2] === 0x46
  ) {
    let bin = "";
    for (let i = 0; i < bytes.length; i += 1) {
      bin += String.fromCharCode(bytes[i]);
    }
    return { kind: "img", src: `data:image/gif;base64,${btoa(bin)}` };
  }

  let text = "";
  try {
    text = new TextDecoder("utf-8", { fatal: false }).decode(bytes).trim();
  } catch {
    return null;
  }
  if (!text) return null;

  if (text.startsWith("data:image") || text.startsWith("data:application")) {
    return { kind: "img", src: text };
  }

  const trimmed = text.trim();
  if (trimmed.startsWith("<svg") || trimmed.startsWith("<?xml")) {
    return {
      kind: "img",
      src: `data:image/svg+xml;charset=utf-8,${encodeURIComponent(trimmed)}`,
    };
  }

  const cleaned = trimmed.replace(/\s/g, "");
  if (
    cleaned.length >= 40 &&
    /^[A-Za-z0-9+/]+=*$/.test(cleaned)
  ) {
    return { kind: "img", src: `data:image/png;base64,${cleaned}` };
  }

  return { kind: "text", text: trimmed };
}

function rowToFormForUpdate(row) {
  return {
    procedure_revision_ref: row.procedure_revision_ref ?? row.part_number ?? "",
    date_of_training: row.date_of_training
      ? String(row.date_of_training).split("T")[0]
      : "",
    trainer_name: getTrainerName(row),
    trainee_name: getTraineeName(row),
    trainer_signature: "",
    trainee_signature: "",
    revision_trained: getRevisionDisplay(row),
    training_status_of_trainee: getTrainingStatusDisplay(row),
  };
}

function signatureRowToStringForApi(row, which) {
  const sig =
    which === "trainer" ? row.trainer_signature : row.trainee_signature;
  if (typeof sig === "string") return sig;
  if (sig?.data) {
    const d = decodeSignatureBuffer(sig);
    if (d?.kind === "img") return d.src;
    if (d?.kind === "text") return d.text;
  }
  return "";
}

const TRAINING_LOG_TABLE_COLUMNS = [
  {
    headName: "Document Number",
    field: "document_number",
    sort: true,
    filter: true,
    required: true,
    width: "10%",
  },
  {
    headName: "Date of Training",
    field: "date_of_training",
    sort: true,
    filter: true,
    required: true,
    width: "10%",
  },
  {
    headName: "Trainer",
    field: "trainer",
    sort: true,
    filter: true,
    required: true,
    width: "10%",
  },
  {
    headName: "Trainer Signature",
    field: "trainer_signature",
    sort: true,
    filter: true,
    required: true,
    width: "12%",
  },
  {
    headName: "Trainee",
    field: "trainee",
    sort: true,
    filter: true,
    required: true,
    width: "10%",
  },
  {
    headName: "Trainee Signature",
    field: "trainee_signature",
    sort: true,
    filter: true,
    required: true,
    width: "12%",
  },
  {
    headName: "Revision Trained",
    field: "revision_trained",
    sort: true,
    filter: true,
    required: true,
    width: "8%",
  },
  {
    headName: "Training Status of Trainee",
    field: "training_status_of_trainee",
    sort: true,
    filter: true,
    required: true,
    width: "12%",
  },
  {
    headName: "Action",
    field: "action",
    textCenter: true,
    isAction: true,
    sort: false,
    filter: false,
  },
];

const TRAINING_LOG_FILTER_FIELDS = TRAINING_LOG_TABLE_COLUMNS.filter(
  (c) => c.filter
).map((c) => c.field);

const TrainingLogTable = () => {
  const tableContainerRef = useRef();
  const fileInputRef = useRef();
  const dispatch = useDispatch();
  const authUser = useSelector((state) => state.auth.user);
  const { trainingLogs, loading } = useSelector((state) => state.trainingLog);
  const permissionCtx = buildPermissionContext(authUser);
  const canEdit = permissionCtx.permissions?.Edit === true;

  const [limit, setLimit] = useState(10);
  const [offset, setOffset] = useState(0);
  const [tableReqPayload, setTableReqPayload] = useState("");
  const [newRow, setNewRow] = useState(null);
  const [editRow, setEditRow] = useState(null);
  const [showLoader, setShowLoader] = useState(false);
  const [signatureModalState, setSignatureModalState] = useState(null);

  const snackbar = (message, type) => {
    dispatch(showSnackbar({ type, message }));
  };

  useEffect(() => {
    dispatch(getAllTrainingLogs({ limit: 5000, offset: 0 }));
  }, [dispatch]);

  const { search, searchTypes, sortBy, sortOrder } = useMemo(
    () => parseTableReqPayload(tableReqPayload),
    [tableReqPayload]
  );

  const processedRows = useMemo(() => {
    let rows = Array.isArray(trainingLogs) ? [...trainingLogs] : [];
    const q = (search || "").trim().toLowerCase();
    if (q) {
      const types =
        searchTypes.length > 0 ? searchTypes : TRAINING_LOG_FILTER_FIELDS;
      rows = rows.filter((row) =>
        types.some((field) =>
          getCellSearchString(row, field).toLowerCase().includes(q)
        )
      );
    }
    return sortRows(rows, sortBy, sortOrder);
  }, [trainingLogs, search, searchTypes, sortBy, sortOrder]);

  const paginationMeta = useMemo(() => {
    const total = processedRows.length;
    const totalPages = Math.max(1, Math.ceil(total / limit) || 1);
    return { total, totalPages };
  }, [processedRows.length, limit]);

  useEffect(() => {
    if (offset >= processedRows.length && processedRows.length > 0) {
      setOffset(Math.max(0, Math.floor((processedRows.length - 1) / limit) * limit));
    }
  }, [processedRows.length, offset, limit]);

  const pageRows = useMemo(
    () => processedRows.slice(offset, offset + limit),
    [processedRows, offset, limit]
  );

  const buildAuditTrail = async () => {
    const hash = await generateSHA256(Math.random().toString());
    return {
      user_ref: localStorage.getItem("user_id") || "",
      ip_address: "",
      device_info: typeof navigator !== "undefined" ? navigator.userAgent : "",
      hash,
      signature: "",
    };
  };

  const buildPayloadFromForm = async (form, isEdit) => {
    const audit_trail_data = {
      ...(await buildAuditTrail()),
      action: isEdit ? "UPDATE_TRAINING_LOG" : "CREATE_TRAINING_LOG",
      comment: isEdit ? "Updated Training Log" : "Created Training Log",
      date: new Date().toISOString(),
      version_content: {},
    };

    const trainer = splitFullName(form.trainer_name);
    const trainee = splitFullName(form.trainee_name);

    return {
      procedure_revision_ref: form.procedure_revision_ref?.trim() || "",
      date_of_training: form.date_of_training,
      trainer: {
        first_name: trainer.first_name,
        last_name: trainer.last_name,
      },
      trainer_signature: form.trainer_signature || "",
      trainee: {
        first_name: trainee.first_name,
        last_name: trainee.last_name,
      },
      trainee_signature: form.trainee_signature || "",
      revision_trained: form.revision_trained?.trim() || "",
      training_status_of_trainee: form.training_status_of_trainee?.trim() || "",
      audit_trail_data,
    };
  };

  const validateForm = (form) => {
    if (!form.procedure_revision_ref?.trim()) {
      snackbar("Document Number is required.", "error");
      return false;
    }
    if (!form.date_of_training) {
      snackbar("Date of Training is required.", "error");
      return false;
    }
    if (!form.trainer_name?.trim()) {
      snackbar("Trainer is required.", "error");
      return false;
    }
    if (!form.trainee_name?.trim()) {
      snackbar("Trainee is required.", "error");
      return false;
    }
    if (!form.revision_trained?.trim()) {
      snackbar("Revision Trained is required.", "error");
      return false;
    }
    if (!form.training_status_of_trainee?.trim()) {
      snackbar("Training Status of Trainee is required.", "error");
      return false;
    }
    return true;
  };

  const handleAddRow = async () => {
    if (!validateForm(newRow)) return;
    setShowLoader(true);
    try {
      const data = await buildPayloadFromForm(newRow, false);
      await dispatch(createTrainingLog(data)).unwrap();
      snackbar("Training log created.", "success");
      setNewRow(null);
      await dispatch(getAllTrainingLogs({ limit: 5000, offset: 0 }));
    } catch (e) {
      const m =
        (typeof e?.payload === "string" && e.payload) ||
        e?.payload?.message ||
        e?.message ||
        "Could not create training log.";
      snackbar(m, "error");
    } finally {
      setShowLoader(false);
    }
  };

  const handleUpdateRow = async () => {
    if (!validateForm(editRow)) return;
    setShowLoader(true);
    try {
      const data = await buildPayloadFromForm(editRow, true);
      const id = editRow.training_log_id;
      await dispatch(updateTrainingLog({ id, data })).unwrap();
      snackbar("Training log updated.", "success");
      setEditRow(null);
      await dispatch(getAllTrainingLogs({ limit: 5000, offset: 0 }));
    } catch (e) {
      const m =
        (typeof e?.payload === "string" && e.payload) ||
        e?.payload?.message ||
        e?.message ||
        "Could not update training log.";
      snackbar(m, "error");
    } finally {
      setShowLoader(false);
    }
  };

  const handleDelete = (id) => {
    DeleteConfirmation(async () => {
      setShowLoader(true);
      try {
        await dispatch(deleteTrainingLog(id)).unwrap();
        snackbar("Training log deleted.", "success");
        await dispatch(getAllTrainingLogs({ limit: 5000, offset: 0 }));
      } catch (e) {
        snackbar(e?.message || "Delete failed.", "error");
      } finally {
        setShowLoader(false);
      }
    }, "Are you sure you want to delete this training log?");
  };

  const handleSignatureModalConfirm = async (payload, row) => {
    if (payload?.error) {
      snackbar(payload.error, "error");
      return { error: payload.error };
    }
    const { password, signatureDataUrl, signedOutsideSystem, role } = payload;

    /* Add Row: no server id yet — store signature on draft state until Save creates the log. */
    if (!row?.training_log_id) {
      if (newRow) {
        setNewRow((prev) => {
          if (!prev) return prev;
          if (role === "trainer") {
            return {
              ...prev,
              trainer_signature: signedOutsideSystem ? "" : signatureDataUrl,
            };
          }
          return {
            ...prev,
            trainee_signature: signedOutsideSystem ? "" : signatureDataUrl,
          };
        });
        snackbar(
          "Signature saved on this row. Use Save to create the log.",
          "success",
        );
        return {};
      }
      snackbar("No training log row.", "error");
      return { error: "Invalid row." };
    }
    setShowLoader(true);
    try {
      const form = rowToFormForUpdate(row);
      if (role === "trainer") {
        form.trainer_signature = signedOutsideSystem ? "" : signatureDataUrl;
        form.trainee_signature = signatureRowToStringForApi(row, "trainee");
      } else {
        form.trainee_signature = signedOutsideSystem ? "" : signatureDataUrl;
        form.trainer_signature = signatureRowToStringForApi(row, "trainer");
      }
      const data = await buildPayloadFromForm(form, true);
      data.is_maintained_outside_system = signedOutsideSystem;
      data.audit_trail_data = {
        ...data.audit_trail_data,
        signature: password,
      };
      if (typeof row.trainer === "string") data.trainer = row.trainer;
      if (typeof row.trainee === "string") data.trainee = row.trainee;
      await dispatch(
        updateTrainingLog({ id: row.training_log_id, data }),
      ).unwrap();
      snackbar("Signature saved.", "success");
      await dispatch(getAllTrainingLogs({ limit: 5000, offset: 0 }));
      setEditRow((prev) => {
        if (!prev || prev.training_log_id !== row.training_log_id) return prev;
        if (role === "trainer") {
          return {
            ...prev,
            trainer_signature: signedOutsideSystem ? "" : signatureDataUrl,
          };
        }
        return {
          ...prev,
          trainee_signature: signedOutsideSystem ? "" : signatureDataUrl,
        };
      });
      return {};
    } catch (e) {
      snackbar(e?.message || "Could not save signature.", "error");
      return { error: e?.message || "Save failed" };
    } finally {
      setShowLoader(false);
    }
  };

  const handleExportCSV = () => {
    const headers = [
      "Document Number",
      "Date of Training",
      "Trainer",
      "Trainer Signature",
      "Trainee",
      "Trainee Signature",
      "Revision Trained",
      "Training Status of Trainee",
    ];
    const lines = [headers.join(",")];
    processedRows.forEach((row) => {
      const vals = [
        getDocumentNumber(row),
        row.date_of_training
          ? String(row.date_of_training).split("T")[0]
          : "",
        getTrainerName(row),
        typeof row.trainer_signature === "string"
          ? row.trainer_signature.replace(/"/g, '""')
          : "",
        getTraineeName(row),
        typeof row.trainee_signature === "string"
          ? row.trainee_signature.replace(/"/g, '""')
          : "",
        getRevisionDisplay(row),
        getTrainingStatusDisplay(row).replace(/"/g, '""'),
      ];
      lines.push(vals.map((v) => `"${String(v)}"`).join(","));
    });
    const blob = new Blob([lines.join("\n")], {
      type: "text/csv;charset=utf-8;",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "training_log_export.csv";
    a.click();
    URL.revokeObjectURL(url);
    snackbar("CSV exported.", "success");
  };

  const handleCsvUpload = (e) => {
    if (e?.target) e.target.value = "";
    snackbar(
      "Bulk CSV import for training logs is not available yet. Use Add Row or edit on the training log page.",
      "info"
    );
  };

  const renderSignatureDisplay = (row, which) => {
    const sig = which === "trainer" ? row.trainer_signature : row.trainee_signature;
    if (sig && typeof sig === "object" && sig.data) {
      const decoded = decodeSignatureBuffer(sig);
      if (!decoded) {
        return (
          <span style={{ ...SIGNATURE_TEXT_STYLE, fontSize: 13 }}>
            Signed outside of System
          </span>
        );
      }
      if (decoded.kind === "img") {
        return (
          <img
            src={decoded.src}
            alt=""
            style={{
              maxHeight: 44,
              maxWidth: "100%",
              objectFit: "contain",
              display: "block",
            }}
          />
        );
      }
      return <span style={SIGNATURE_TEXT_STYLE}>{decoded.text}</span>;
    }
    if (typeof sig === "string" && sig.trim()) {
      const imgSrc = signatureStringToImgSrc(sig);
      if (imgSrc) {
        return (
          <img
            src={imgSrc}
            alt=""
            style={{
              maxHeight: 44,
              maxWidth: "100%",
              objectFit: "contain",
              display: "block",
            }}
          />
        );
      }
      return <span style={SIGNATURE_TEXT_STYLE}>{sig.trim()}</span>;
    }
    return (
      <span style={{ ...SIGNATURE_TEXT_STYLE, fontSize: 13 }}>
        Signed outside of System
      </span>
    );
  };

  const inputStyle = {
    height: 28,
    fontSize: 13,
    width: "100%",
    boxSizing: "border-box",
    border: "1px solid #0D6EFD",
    borderRadius: 4,
    padding: "2px 6px",
  };

  const renderNewRow = () => {
    if (!newRow) return null;
    return (
      <tr>
        <td>
          <input
            type="text"
            style={inputStyle}
            value={newRow.procedure_revision_ref}
            onChange={(e) =>
              setNewRow((p) => ({ ...p, procedure_revision_ref: e.target.value }))
            }
          />
        </td>
        <td>
          <input
            type="date"
            style={inputStyle}
            value={newRow.date_of_training}
            onChange={(e) =>
              setNewRow((p) => ({ ...p, date_of_training: e.target.value }))
            }
          />
        </td>
        <td>
          <input
            type="text"
            style={inputStyle}
            value={newRow.trainer_name}
            onChange={(e) =>
              setNewRow((p) => ({ ...p, trainer_name: e.target.value }))
            }
          />
        </td>
        <td>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: canEdit ? "space-between" : "flex-start",
              width: "100%",
              minHeight: 36,
              gap: 8,
            }}
          >
            <div style={{ flex: 1, minWidth: 0, textAlign: "left" }}>
              {renderSignatureDisplay(newRow, "trainer")}
            </div>
            {canEdit && (
              <button
                type="button"
                className="app-table-signature-edit"
                title="Sign trainer signature"
                style={{
                  border: "none",
                  background: "none",
                  cursor: "pointer",
                  padding: 0,
                  display: "flex",
                  flexShrink: 0,
                  alignSelf: "center",
                }}
                onClick={() =>
                  setSignatureModalState({ role: "trainer", row: newRow })
                }
              >
                <SignatureCellEditIcon />
              </button>
            )}
          </div>
        </td>
        <td>
          <input
            type="text"
            style={inputStyle}
            value={newRow.trainee_name}
            onChange={(e) =>
              setNewRow((p) => ({ ...p, trainee_name: e.target.value }))
            }
          />
        </td>
        <td>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: canEdit ? "space-between" : "flex-start",
              width: "100%",
              minHeight: 36,
              gap: 8,
            }}
          >
            <div style={{ flex: 1, minWidth: 0, textAlign: "left" }}>
              {renderSignatureDisplay(newRow, "trainee")}
            </div>
            {canEdit && (
              <button
                type="button"
                title="Sign trainee signature"
                style={{
                  border: "none",
                  background: "none",
                  cursor: "pointer",
                  padding: 0,
                  display: "flex",
                  flexShrink: 0,
                  alignSelf: "center",
                }}
                onClick={() =>
                  setSignatureModalState({ role: "trainee", row: newRow })
                }
              >
                <SignatureCellEditIcon />
              </button>
            )}
          </div>
        </td>
        <td>
          <input
            type="text"
            style={inputStyle}
            value={newRow.revision_trained}
            onChange={(e) =>
              setNewRow((p) => ({ ...p, revision_trained: e.target.value }))
            }
          />
        </td>
        <td>
          <input
            type="text"
            style={inputStyle}
            value={newRow.training_status_of_trainee}
            onChange={(e) =>
              setNewRow((p) => ({
                ...p,
                training_status_of_trainee: e.target.value,
              }))
            }
          />
        </td>
        <td>
          <TableActionGroup
            mode="editing"
            onSave={handleAddRow}
            onCancel={() => setNewRow(null)}
          />
        </td>
      </tr>
    );
  };

  const renderEditRow = (row) => {
    if (!editRow || editRow.training_log_id !== row.training_log_id) return null;
    return (
      <tr key={`edit-${row.training_log_id}`}>
        <td>
          <input
            type="text"
            style={inputStyle}
            value={editRow.procedure_revision_ref}
            onChange={(e) =>
              setEditRow((p) => ({ ...p, procedure_revision_ref: e.target.value }))
            }
          />
        </td>
        <td>
          <input
            type="date"
            style={inputStyle}
            value={editRow.date_of_training}
            onChange={(e) =>
              setEditRow((p) => ({ ...p, date_of_training: e.target.value }))
            }
          />
        </td>
        <td>
          <input
            type="text"
            style={inputStyle}
            value={editRow.trainer_name}
            onChange={(e) =>
              setEditRow((p) => ({ ...p, trainer_name: e.target.value }))
            }
          />
        </td>
        <td>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: canEdit ? "space-between" : "flex-start",
              width: "100%",
              minHeight: 36,
              gap: 8,
            }}
          >
            <div style={{ flex: 1, minWidth: 0, textAlign: "left" }}>
              {renderSignatureDisplay(editRow, "trainer")}
            </div>
            {canEdit && (
              <button
                type="button"
                className="app-table-signature-edit"
                title="Sign trainer signature"
                style={{
                  border: "none",
                  background: "none",
                  cursor: "pointer",
                  padding: 0,
                  display: "flex",
                  flexShrink: 0,
                  alignSelf: "center",
                }}
                onClick={() =>
                  setSignatureModalState({ role: "trainer", row: editRow })
                }
              >
                <SignatureCellEditIcon />
              </button>
            )}
          </div>
        </td>
        <td>
          <input
            type="text"
            style={inputStyle}
            value={editRow.trainee_name}
            onChange={(e) =>
              setEditRow((p) => ({ ...p, trainee_name: e.target.value }))
            }
          />
        </td>
        <td>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: canEdit ? "space-between" : "flex-start",
              width: "100%",
              minHeight: 36,
              gap: 8,
            }}
          >
            <div style={{ flex: 1, minWidth: 0, textAlign: "left" }}>
              {renderSignatureDisplay(editRow, "trainee")}
            </div>
            {canEdit && (
              <button
                type="button"
                title="Sign trainee signature"
                style={{
                  border: "none",
                  background: "none",
                  cursor: "pointer",
                  padding: 0,
                  display: "flex",
                  flexShrink: 0,
                  alignSelf: "center",
                }}
                onClick={() =>
                  setSignatureModalState({ role: "trainee", row: editRow })
                }
              >
                <SignatureCellEditIcon />
              </button>
            )}
          </div>
        </td>
        <td>
          <input
            type="text"
            style={inputStyle}
            value={editRow.revision_trained}
            onChange={(e) =>
              setEditRow((p) => ({ ...p, revision_trained: e.target.value }))
            }
          />
        </td>
        <td>
          <input
            type="text"
            style={inputStyle}
            value={editRow.training_status_of_trainee}
            onChange={(e) =>
              setEditRow((p) => ({
                ...p,
                training_status_of_trainee: e.target.value,
              }))
            }
          />
        </td>
        <td>
          <TableActionGroup
            mode="editing"
            onSave={handleUpdateRow}
            onCancel={() => setEditRow(null)}
          />
        </td>
      </tr>
    );
  };

  const beginEdit = (row) => {
    if (editRow !== null || newRow !== null) {
      snackbar(
        "Please save or cancel the current row before adding or editing another one.",
        "error"
      );
      return;
    }
    setEditRow(rowToEditShape(row));
  };

  return (
    <>
      <AppTable
        ref={tableContainerRef}
        columnsData={TRAINING_LOG_TABLE_COLUMNS}
        showSearchInput
        paginate
        pagination={{
          limit: [limit, setLimit],
          offset: [offset, setOffset],
          pagination: paginationMeta,
          maxVsbleButtons: 7,
          paginationPosition: {
            position: "center",
            leftContent: (
              <>
                {canEdit && (
                  <AddBtn
                    onClick={() => {
                      if (editRow !== null) {
                        snackbar(
                          "Please save or cancel the current row before adding or editing another one.",
                          "error"
                        );
                        return;
                      }
                      if (newRow !== null) {
                        snackbar(
                          "Please save or cancel the current row before adding or editing another one.",
                          "error"
                        );
                        return;
                      }
                      scrollToTop(tableContainerRef);
                      setNewRow(emptyNewRow());
                    }}
                  />
                )}
              </>
            ),
            rightContent: (
              <>
                <UploadViaCsvBtn
                  fileInputRef={fileInputRef}
                  handleFileChange={handleCsvUpload}
                  disabled={!canEdit}
                />{" "}
                &nbsp;
                <ExportCsvBtn handleExportCSV={handleExportCSV} />
              </>
            ),
          },
          paginationShadow: true,
        }}
        usingInPopup
        onFilterSortHandler={setTableReqPayload}
      >
        {canEdit && renderNewRow()}
        {pageRows.map((row, i) => {
          const isEditing =
            editRow && editRow.training_log_id === row.training_log_id;
          if (isEditing) {
            return renderEditRow(row);
          }
          return (
            <tr key={row.training_log_id ?? `row-${offset + i}`}>
              <td>{getDocumentNumber(row)}</td>
              <td>
                {row.date_of_training
                  ? String(row.date_of_training).split("T")[0]
                  : ""}
              </td>
              <td>{getTrainerName(row)}</td>
              <td>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: canEdit ? "space-between" : "flex-start",
                    width: "100%",
                    minHeight: 36,
                    gap: 8,
                  }}
                >
                  <div style={{ flex: 1, minWidth: 0, textAlign: "left" }}>
                    {renderSignatureDisplay(row, "trainer")}
                  </div>
                  {canEdit && (
                    <button
                      type="button"
                      className="app-table-signature-edit"
                      title="Edit row"
                      style={{
                        border: "none",
                        background: "none",
                        cursor: "pointer",
                        padding: 0,
                        display: "flex",
                        flexShrink: 0,
                        alignSelf: "center",
                      }}
                      onClick={() =>
                        setSignatureModalState({ role: "trainer", row })
                      }
                    >
                      <SignatureCellEditIcon />
                    </button>
                  )}
                </div>
              </td>
              <td>{getTraineeName(row)}</td>
              <td>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: canEdit ? "space-between" : "flex-start",
                    width: "100%",
                    minHeight: 36,
                    gap: 8,
                  }}
                >
                  <div style={{ flex: 1, minWidth: 0, textAlign: "left" }}>
                    {renderSignatureDisplay(row, "trainee")}
                  </div>
                  {canEdit && (
                    <button
                      type="button"
                      title="Edit row"
                      style={{
                        border: "none",
                        background: "none",
                        cursor: "pointer",
                        padding: 0,
                        display: "flex",
                        flexShrink: 0,
                        alignSelf: "center",
                      }}
                      onClick={() =>
                        setSignatureModalState({ role: "trainee", row })
                      }
                    >
                      <SignatureCellEditIcon />
                    </button>
                  )}
                </div>
              </td>
              <td>{getRevisionDisplay(row)}</td>
              <td>{getTrainingStatusDisplay(row)}</td>
              <td>
                <TableActionGroup
                  mode="view"
                  showEdit={canEdit}
                  showDelete={canEdit}
                  onEdit={() => beginEdit(row)}
                  onDelete={() =>
                    handleDelete(row.training_log_id ?? row.uuid ?? row.id)
                  }
                />
              </td>
            </tr>
          );
        })}
      </AppTable>
      {signatureModalState && (
        <TrainingLogSignatureModal
          closeModal={() => setSignatureModalState(null)}
          role={signatureModalState.role}
          partNumber={
            signatureModalState.row.part_number ??
            getDocumentNumber(signatureModalState.row)
          }
          revision={
            signatureModalState.row.rev ??
            getRevisionDisplay(signatureModalState.row)
          }
          trainerName={getTrainerName(signatureModalState.row)}
          traineeName={getTraineeName(signatureModalState.row)}
          onConfirm={(payload) =>
            handleSignatureModalConfirm(payload, signatureModalState.row)
          }
        />
      )}
      {(showLoader || loading) && <Loader />}
    </>
  );
};

export default TrainingLogTable;
